/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

// App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import TeacherPanel from './components/teacher/TeacherPanel';
import AdminPanel from './components/admin/AdminPanel';
import AvailableTests from './components/AvailableTests';
import HomePage from './components/HomePage';
import DemoTermsPage from './components/DemoTermsPage';
import LoginForm from './components/LoginForm';
import RegistrationForm from './components/auth/RegistrationForm';
import StaffLoginForm from './components/auth/StaffLoginForm';
import SetPasswordForm from './components/auth/SetPasswordForm';
import TestTaking from './components/TestTaking';
import ReviewFailedTest from './components/ReviewFailedTest';
import StudentGradebook from './components/student/StudentGradebook';
import { Button } from './components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './components/ui/dialog';
import UpdateNotification from './components/UpdateNotification';
import ThemeSelector from './components/ThemeSelector';
import { BrandingProvider, useBranding } from './contexts/BrandingContext';
import logger from './utils/logger';
import analytics from './utils/analytics';

// Layout component that includes the navigation header
function AppLayout({ user, isUserView, isTeacherView, handleSwitchToAdminView, handleSwitchToUserView, handleSwitchToTeacherView, handleLogout, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { branding } = useBranding();

  // Routes that shouldn't show the banner
  const hideBannerRoutes = ['/', '/login', '/register', '/staff-login', '/set-password', '/demo-terms'];
  const shouldShowBanner = user && !hideBannerRoutes.includes(location.pathname);

  const handleNavigate = (path) => {
    navigate(path);
  };

  // Determine if we should hide page overflow (only for teacher panel)
  // TeacherPanel manages its own internal scrolling for gradebook vs other sections
  // Admin and student views need normal page scrolling
  const isTeacherPanel = location.pathname.startsWith('/teacher');

  return (
    <div
      className={`App h-screen flex flex-col ${isTeacherPanel ? 'overflow-hidden' : 'overflow-auto'}`}
      style={{
        backgroundColor: 'var(--color-background)',
        color: 'var(--color-text)'
      }}
    >
      <UpdateNotification />
      {shouldShowBanner && (
        <nav className="bg-blue-600 text-white p-4 flex-shrink-0" style={{ backgroundColor: 'var(--color-primary)' }}>
          <div className="w-full px-4 flex justify-between items-center">
            <div className="flex-shrink-0">
              <div className="flex items-center gap-3">
                {branding.logo && (
                  <img
                    src={branding.logo}
                    alt={`${branding.schoolName} logo`}
                    className="h-12 w-12 object-contain rounded"
                  />
                )}
                <div className="flex flex-col">
                  <span className="text-white font-bold text-lg">{branding.schoolName}</span>
                  <span className="text-white text-sm opacity-90">Welcome, {user.firstName}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 sm:gap-4 flex-shrink-0">
              {/* Show appropriate navigation buttons based on current view and user role */}
              {user.role === 'admin' && !isUserView && !isTeacherView && (
                <>
                  <Button onClick={handleSwitchToTeacherView} className="text-sm sm:text-base px-2 sm:px-4">
                    Switch to Teacher View
                  </Button>
                  <Button onClick={handleSwitchToUserView} className="text-sm sm:text-base px-2 sm:px-4">
                    Switch to Student View
                  </Button>
                </>
              )}
              {user.role === 'admin' && isTeacherView && (
                <>
                  <Button onClick={handleSwitchToAdminView} className="text-sm sm:text-base px-2 sm:px-4">
                    Return to Admin Panel
                  </Button>
                  <Button onClick={handleSwitchToUserView} className="text-sm sm:text-base px-2 sm:px-4">
                    Switch to Student View
                  </Button>
                </>
              )}
              {user.role === 'admin' && isUserView && (
                <>
                  <Button onClick={handleSwitchToAdminView} className="text-sm sm:text-base px-2 sm:px-4">
                    Return to Admin Panel
                  </Button>
                  <Button onClick={handleSwitchToTeacherView} className="text-sm sm:text-base px-2 sm:px-4">
                    Switch to Teacher View
                  </Button>
                </>
              )}
              {user.role === 'teacher' && !isUserView && (
                <Button onClick={handleSwitchToUserView} className="text-sm sm:text-base px-2 sm:px-4">
                  Switch to Student View
                </Button>
              )}
              {user.role === 'teacher' && isUserView && (
                <Button onClick={handleSwitchToAdminView} className="text-sm sm:text-base px-2 sm:px-4">
                  Return to Teacher Panel
                </Button>
              )}
              {/* Student navigation: My Tests | My Grades */}
              {(user.role === 'student' || isUserView) && location.pathname !== '/login' && location.pathname !== '/register' && (
                <>
                  <Button
                    onClick={() => handleNavigate('/available-tests')}
                    variant={location.pathname.startsWith('/grades') ? 'default' : 'secondary'}
                    className="text-sm sm:text-base px-2 sm:px-4 hover:bg-blue-600 hover:text-white transition-colors"
                  >
                    My Tests
                  </Button>
                  <Button
                    onClick={() => handleNavigate('/grades')}
                    variant={location.pathname.startsWith('/grades') ? 'secondary' : 'default'}
                    className="text-sm sm:text-base px-2 sm:px-4 hover:bg-blue-600 hover:text-white transition-colors"
                  >
                    My Grades
                  </Button>
                </>
              )}
              {/* Theme selector */}
              <ThemeSelector />
              {/* All users get logout */}
              <Button onClick={handleLogout} className="text-sm sm:text-base px-2 sm:px-4">Logout</Button>
            </div>
          </div>
        </nav>
      )}
      <div className={`flex-grow flex ${isTeacherPanel ? 'overflow-hidden' : 'overflow-auto'} ${shouldShowBanner ? (isTeacherPanel ? 'items-stretch' : 'items-start p-4') : 'items-start md:items-center p-4'} justify-center`}>
        {children}
      </div>
    </div>
  );
}

function ProtectedRoute({ user, allowedRoles = [], children }) {
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/available-tests" replace />;
  }

  return children;
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [isUserView, setIsUserView] = useState(false);
  const [isTeacherView, setIsTeacherView] = useState(false);
  const [currentTestId, setCurrentTestId] = useState(null);
  const [reviewTestId, setReviewTestId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showTestResultModal, setShowTestResultModal] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to validate if a path is accessible for a user role
  const validatePathForUser = (path, role, isAdmin) => {
    // Admin/teacher paths
    if (path.startsWith('/admin')) {
      return isAdmin || role === 'admin';
    }
    if (path.startsWith('/teacher')) {
      return role === 'teacher' || isAdmin || role === 'admin';
    }
    // Student paths (accessible to all)
    if (path.startsWith('/available-tests') || path.startsWith('/grades') || path.startsWith('/test/') || path.startsWith('/review/')) {
      return true;
    }
    // Login/register/auth (accessible to all)
    if (path === '/' || path === '/login' || path === '/register' || path === '/staff-login' || path === '/set-password') {
      return true;
    }
    // Unknown path, don't allow
    return false;
  };

  // Helper function to navigate user to appropriate home based on role
  const navigateToHome = (role, isAdmin) => {
    if (isAdmin || role === 'admin') {
      navigate('/admin', { replace: true });
    } else if (role === 'teacher') {
      navigate('/teacher', { replace: true });
    } else {
      navigate('/available-tests', { replace: true });
    }
  };

  useEffect(() => {
    logger.info('page_navigation', {
      path: location.pathname,
      userRole: user?.role
    });
    
    // Track page views in Google Analytics
    analytics.trackPageView(location.pathname, document.title);
  }, [location, user]);

  // Session Check
  const checkSession = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/check`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Session check failed');
      }

      return await response.json();
    } catch (error) {
      logger.error('session_check_failed', {
        error: error.message,
        timestamp: new Date().toISOString()
      });
      return null;
    }
  };
      
  useEffect(() => {
    const verifySession = async () => {
      const sessionData = await checkSession();
      if (sessionData?.isAuthenticated) {
        setUser({
          id: sessionData.userId,
          role: sessionData.role,
          isAdmin: sessionData.isAdmin,
          firstName: sessionData.firstName || 'User'
        });

        // Set GA user properties for session restoration (non-blocking)
        try {
          analytics.setUserProperties(sessionData.userId, sessionData.role);
        } catch (error) {
          console.warn('Analytics tracking failed during session restoration:', error);
        }

        logger.info('session_restored', {
          userId: sessionData.userId,
          role: sessionData.role
        });

        // Check if we need to restore path after update/refresh
        const returnPath = localStorage.getItem('refresh_return_path');
        const currentPath = window.location.pathname + window.location.search;

        if (returnPath && returnPath !== currentPath) {
          console.log('Attempting path restoration:', { returnPath, currentPath });
          localStorage.removeItem('refresh_return_path');

          // Validate the path is accessible for this user role
          const isValidPath = validatePathForUser(returnPath, sessionData.role, sessionData.isAdmin);

          if (isValidPath) {
            logger.info('path_restored_after_update', {
              userId: sessionData.userId,
              path: returnPath
            });
            console.log('Navigating to saved path:', returnPath);
            navigate(returnPath, { replace: true });
          } else {
            // Path not valid for user, navigate to appropriate home
            logger.info('invalid_path_after_update', {
              userId: sessionData.userId,
              attemptedPath: returnPath,
              redirecting: true
            });
            console.log('Invalid path, navigating to home');
            navigateToHome(sessionData.role, sessionData.isAdmin);
          }
        } else if (returnPath) {
          console.log('Already at return path, clearing:', returnPath);
          localStorage.removeItem('refresh_return_path');
        }
      } else {
        // No session, clear any saved return path
        localStorage.removeItem('refresh_return_path');
      }
    };

    verifySession();
  }, [navigate]);

  const handleSuccessfulLogin = (userData) => {
    const user = {
      ...userData,
      firstName: userData.firstName || userData.first_name,
    };
    
    setUser(user);
    
    // Store sessionId in localStorage for logger
    if (userData.sessionId) {
      localStorage.setItem('sessionId', userData.sessionId);
    }
    
    // Track login event and set user properties (non-blocking)
    try {
      analytics.trackLogin(user.id, user.role);
      analytics.setUserProperties(user.id, user.role);
    } catch (error) {
      console.warn('Analytics tracking failed during login:', error);
    }
    
    logger.info('user_login_success', {
      userId: user.id,
      role: user.role,
      timestamp: new Date().toISOString()
    });

  checkSession()
    .then(sessionData => {
      if (!sessionData?.isAuthenticated) {
        logger.error('session_establishment_failed', {
          userId: user.id,
          timestamp: new Date().toISOString()
        });
        handleLogout();
        return;
      }

      switch (user.role) {
        case 'admin':
          logger.debug('routing_to_admin');
          navigate('/admin');
          break;
        case 'teacher':
          logger.debug('routing_to_teacher');
          navigate('/teacher');
          break;
        default:
          logger.debug('routing_to_available_tests');
          navigate('/available-tests');
      }
    })
    .catch(error => {
      logger.error('login_session_check_failed', {
        error: error.message,
        userId: user.id,
        timestamp: new Date().toISOString()
      });
      handleLogout();
    });
  };

  const handleSuccessfulRegister = () => {
    // Track registration event (non-blocking)
    try {
      analytics.trackRegistration('student'); // New registrations are typically students
    } catch (error) {
      console.warn('Analytics tracking failed during registration:', error);
    }
    navigate('/');
  };

  const handleStartTest = (testId) => {
    setCurrentTestId(testId);
    navigate(`/test/${testId}`);
  };

  const handleReviewTest = (testId) => {
    setReviewTestId(testId);
    navigate(`/review/${testId}`);
  };

  const handleTestComplete = (score, total, percentage, passed, passThreshold = 90) => {
    setTestResult({ score, total, percentage, passed, passThreshold });
    setShowTestResultModal(true);
  };

  const handleCloseTestResult = () => {
    setShowTestResultModal(false);
    setTestResult(null);
    navigate('/available-tests');
  };

  const handleBackToTests = () => {
    navigate('/available-tests');
  };

  const handleSwitchToUserView = () => {
    // Track role switching for analytics
    const currentView = isTeacherView ? 'teacher' : 'admin';
    analytics.trackRoleSwitch(currentView, 'student');
    
    setIsUserView(true);
    setIsTeacherView(false);
    navigate('/available-tests');
  };

  const handleSwitchToTeacherView = () => {
    // Track role switching for analytics
    const currentView = isUserView ? 'student' : 'admin';
    analytics.trackRoleSwitch(currentView, 'teacher');
    
    setIsUserView(false);
    setIsTeacherView(true);
    navigate('/teacher');
  };

  const handleSwitchToAdminView = () => {
    // Track role switching for analytics
    const currentView = isUserView ? 'student' : isTeacherView ? 'teacher' : 'admin';
    analytics.trackRoleSwitch(currentView, 'admin');
    
    setIsUserView(false);
    setIsTeacherView(false);
    if (user.role === 'admin') {
      navigate('/admin');
    } else if (user.role === 'teacher') {
      navigate('/teacher');
    }
  };

// Update the handleLogout function
const handleLogout = async () => {
  try {
    await fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    
    // Track logout event before clearing user data (non-blocking)
    try {
      analytics.trackLogout(user?.id, user?.role);
    } catch (error) {
      console.warn('Analytics tracking failed during logout:', error);
    }
    
    logger.info('user_logout', {
      userId: user?.id,
      role: user?.role,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    logger.error('logout_failed', {
      error: error.message,
      timestamp: new Date().toISOString()
    });
  }

  setUser(null);
  setIsUserView(false);
  navigate('/');
  localStorage.removeItem('user');
};

return (
    <AppLayout 
      user={user} 
      isUserView={isUserView}
      isTeacherView={isTeacherView}
      handleSwitchToAdminView={handleSwitchToAdminView}
      handleSwitchToUserView={handleSwitchToUserView}
      handleSwitchToTeacherView={handleSwitchToTeacherView}
      handleLogout={handleLogout}
    >
      <Routes>
        <Route path="/" element={<HomePage onLoginClick={() => navigate('/login')} onRegisterClick={() => navigate('/register')} />} />
        <Route path="/login" element={<LoginForm onBackToHome={() => navigate('/')} onSuccessfulLogin={handleSuccessfulLogin} />} />
        <Route path="/register" element={<RegistrationForm onBackToHome={() => navigate('/')} onSuccessfulRegister={handleSuccessfulRegister} />} />
        <Route path="/staff-login" element={<StaffLoginForm onSuccessfulLogin={handleSuccessfulLogin} />} />
        <Route path="/set-password" element={<SetPasswordForm onSuccessfulLogin={handleSuccessfulLogin} />} />
        <Route path="/demo-terms" element={<DemoTermsPage />} />

        <Route 
          path="/available-tests" 
          element={
            <ProtectedRoute user={user}>
              <AvailableTests user={user} onStartTest={handleStartTest} onReviewTest={handleReviewTest} />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/test/:testId" 
          element={
            <ProtectedRoute user={user}>
              <TestTaking user={user} testId={currentTestId || location.pathname.split('/')[2]} onTestComplete={handleTestComplete} />
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/review/:testId"
          element={
            <ProtectedRoute user={user}>
              <ReviewFailedTest user={user} testId={reviewTestId || location.pathname.split('/')[2]} onBackToTests={handleBackToTests} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/grades"
          element={
            <ProtectedRoute user={user} allowedRoles={['student', 'teacher', 'admin']}>
              <StudentGradebook user={user} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin" 
          element={
            <ProtectedRoute user={user} allowedRoles={['admin']}>
              <AdminPanel />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/teacher" 
          element={
            <ProtectedRoute user={user} allowedRoles={['teacher', 'admin']}>
              <TeacherPanel user={user} onSwitchToUserView={handleSwitchToUserView} isUserView={isUserView} />
            </ProtectedRoute>
          } 
        />
        
        {/* Redirect unknown routes to homepage */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Test Result Modal */}
      <Dialog open={showTestResultModal} onOpenChange={setShowTestResultModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={testResult?.passed ? "text-green-600" : "text-red-600"}>
              {testResult?.passed ? "Congratulations!" : "Test Complete"}
            </DialogTitle>
            <DialogDescription>
              Your test has been submitted successfully.
            </DialogDescription>
          </DialogHeader>

          {testResult && (
            <div className="space-y-4 py-4">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">
                  {testResult.score}/{testResult.total}
                </div>
                <div className="text-2xl font-semibold mb-4">
                  {testResult.percentage}%
                </div>

                {testResult.passed ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-semibold">You passed!</p>
                    <p className="text-sm text-green-600 mt-1">
                      Great job! You met the required {testResult.passThreshold}% threshold.
                    </p>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 font-semibold">You did not pass</p>
                    <p className="text-sm text-red-600 mt-1">
                      The minimum required score is {testResult.passThreshold}%. Keep studying and try again!
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="sm:justify-center">
            <Button onClick={handleCloseTestResult} className="w-full sm:w-auto">
              Back to My Tests
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function App() {
  return (
    <BrandingProvider>
      <Router>
        <AppContent />
      </Router>
    </BrandingProvider>
  );
}

export default App;